import {
  PARTNER_SETTLEMENT_METHOD_LABELS,
  PARTNER_SETTLEMENT_METHODS,
  type PartnerOutstanding,
  type PartnerSettlementDto,
  type PartnerSettlementMethod,
  reportPeriodRange,
} from '@fos/shared/contracts/time';
import { todayLocal } from '@fos/shared/time';
import { HandCoins, MoreHorizontal, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { formatClock, formatDate, formatDateShort, formatMoneyAuto, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Avatar } from '../../../ui/Avatar';
import { FormMessage } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { useInvalidateTime } from '../hooks/useTimeMutations';
import { rangeLongLabel } from '../time-format';

/**
 * Rozliczenia partnera (/czas?widok=rozliczenia; DECISIONS 2026-10-08 [P] „Rozliczenie między partnerami — godziny
 * wpisywane raz”). Prowadzący (owner / admin): „Nowe rozliczenie” — partner i okres → podgląd godzin i kwoty →
 * „Zamknij rozliczenie” (data, gotówka / przelew / faktura, notatka); „Otwórz ponownie” przy zamkniętym. Manager widzi
 * listę, partner — swoje rozliczenia i to, co czeka na wypłatę. Widok czeka na projekt w Figmie (wzorce: biała sekcja,
 * lista z linią).
 */

export function PartnerSettlements() {
  const list = trpc.time.partnerSettlements.useQuery({});
  const mine = trpc.time.unbilledSummary.useQuery({ scope: 'mine' });
  const session = useActiveSession();
  const [creating, setCreating] = useState(false);
  // „Rozlicz” przy osobie / „Nowe rozliczenie”: początkowe wartości okna (klucz = nowe okno, świeży stan)
  const [draft, setDraft] = useState<SettlementDraft>(() => ({ key: 0 }));
  const [reopening, setReopening] = useState<PartnerSettlementDto | null>(null);
  const openNew = (init: Omit<SettlementDraft, 'key'> = {}) => {
    setDraft((d) => ({ ...init, key: d.key + 1 }));
    setCreating(true);
  };

  if (list.isPending) {
    return (
      <Section>
        <ListSkeleton rows={3} />
      </Section>
    );
  }
  if (list.isError || !list.data) {
    return (
      <Section>
        <ErrorState
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
          description="Nie udało się wczytać rozliczeń."
        />
      </Section>
    );
  }
  const { items, canManage, partners, outstanding } = list.data;
  const pending = mine.data?.partner;
  // Przegląd Fali 11: prowadzący widzi, ile komu jest winien, zanim zamknie rozliczenie (własne — w „Moje do wypłaty”)
  const owed = outstanding.filter((o) => o.user.userId !== session.user.id);
  const today = todayLocal();
  const showPerson = canManage || items.some((s) => s.user.userId !== items[0]?.user.userId);

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      {pending && (
        <Section aria-label="Do wypłaty">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <span className="text-body-s text-fg-secondary">Moje do wypłaty (nierozliczone)</span>
            <span className="flex items-baseline gap-4">
              <Mono className="text-body-s text-fg-muted">{formatClock(pending.seconds)}</Mono>
              <Mono className="text-body-m font-semibold text-fg">
                {formatMoneyAuto(pending.amount, pending.currency)}
              </Mono>
            </span>
          </div>
        </Section>
      )}
      {owed.length > 0 && (
        <Section title="Do wypłaty partnerom">
          <ul className="flex flex-col">
            {owed.map((o) => (
              <li key={o.user.userId} className="flex min-h-[56px] items-center gap-3 border-b border-line py-2">
                <Avatar initials={o.user.initials} color={o.user.avatarColor} title={o.user.name} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-body-s font-medium text-fg">{o.user.name}</span>
                  <span className="truncate text-caption text-fg-muted">
                    {o.entryCount} {plural(o.entryCount, ['wpis', 'wpisy', 'wpisów'])} nierozliczone · od{' '}
                    {formatDateShort(o.from)}
                  </span>
                </div>
                <Mono className="hidden text-body-s text-fg-muted sm:inline">{formatClock(o.seconds)}</Mono>
                <Mono className="text-body-s font-semibold text-fg">{formatMoneyAuto(o.amount, o.currency)}</Mono>
                {canManage ? (
                  <Button
                    variant="secondary"
                    size="s"
                    className={TOUCH_BUTTON}
                    aria-label={`Rozlicz: ${o.user.name}`}
                    onClick={() => openNew({ userId: o.user.userId, from: o.from, to: o.to > today ? o.to : today })}
                  >
                    Rozlicz
                  </Button>
                ) : (
                  <span className="w-8 shrink-0" aria-hidden />
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section
        title={canManage ? 'Rozliczenia partnerów' : 'Rozliczenia z prowadzącym'}
        action={
          canManage ? (
            <Button variant="secondary" size="s" className={TOUCH_BUTTON} icon={HandCoins} onClick={() => openNew()}>
              Nowe rozliczenie
            </Button>
          ) : undefined
        }
      >
        {items.length === 0 ? (
          <EmptyState
            icon={HandCoins}
            compact
            title="Brak rozliczeń"
            description={
              canManage
                ? 'Zamknij rozliczenie partnera za okres: godziny i kwota z jego wpisów, gotówka, przelew albo faktura.'
                : 'Tu zobaczysz rozliczenia, które zamknie prowadzący projekt.'
            }
          />
        ) : (
          <ul className="flex flex-col">
            {items.map((s) => (
              <SettlementRow key={s.id} settlement={s} showPerson={showPerson} onReopen={() => setReopening(s)} />
            ))}
          </ul>
        )}
      </Section>
      {canManage && (
        <NewSettlementDialog
          key={draft.key}
          open={creating}
          onOpenChange={setCreating}
          partners={partners}
          outstanding={outstanding}
          initial={draft}
        />
      )}
      <ReopenDialog settlement={reopening} onClose={() => setReopening(null)} />
    </div>
  );
}

function statusText(s: PartnerSettlementDto): string {
  if (s.status === 'reopened') {
    return `Otwarte ponownie ${s.reopenedAt ? formatDate(s.reopenedAt) : ''}${s.reopenReason ? ` · ${s.reopenReason}` : ''}`;
  }
  return `Rozliczone ${formatDate(s.settledOn)} · ${PARTNER_SETTLEMENT_METHOD_LABELS[s.method]}`;
}

function SettlementRow({
  settlement: s,
  showPerson,
  onReopen,
}: {
  settlement: PartnerSettlementDto;
  showPerson: boolean;
  onReopen: () => void;
}) {
  const reopened = s.status === 'reopened';
  return (
    <li className="flex min-h-[60px] items-center gap-3 border-b border-line py-2">
      {showPerson && <Avatar initials={s.user.initials} color={s.user.avatarColor} title={s.user.name} />}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-body-s font-medium text-fg">
          {showPerson ? `${s.user.name} · ` : ''}
          {rangeLongLabel(s.range)}
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <StatusPill tone={reopened ? 'neutral' : 'success'}>{statusText(s)}</StatusPill>
          {s.note && !reopened && <span className="truncate text-caption text-fg-muted">{s.note}</span>}
        </span>
      </div>
      <Mono className="hidden text-body-s text-fg-muted sm:inline">{formatClock(s.seconds)}</Mono>
      <Mono className={reopened ? 'text-body-s text-fg-muted line-through' : 'text-body-s font-semibold text-fg'}>
        {formatMoneyAuto(s.amount, s.currency)}
      </Mono>
      {s.canReopen ? (
        <Menu>
          <MenuTrigger asChild>
            <IconButton icon={MoreHorizontal} label={`Więcej: rozliczenie ${rangeLongLabel(s.range)}`} />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem icon={RotateCcw} onSelect={() => setTimeout(onReopen, 0)}>
              Otwórz ponownie
            </MenuItem>
          </MenuContent>
        </Menu>
      ) : (
        <span className="w-8 shrink-0" aria-hidden />
      )}
    </li>
  );
}

const METHOD_OPTIONS = PARTNER_SETTLEMENT_METHODS.map((m) => ({
  value: m,
  label: PARTNER_SETTLEMENT_METHOD_LABELS[m].replace(/^./, (c) => c.toUpperCase()),
}));

interface SettlementDraft {
  key: number;
  userId?: string;
  from?: string;
  to?: string;
}

function NewSettlementDialog({
  open,
  onOpenChange,
  partners,
  outstanding,
  initial,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partners: { userId: string; name: string }[];
  outstanding: PartnerOutstanding[];
  initial: Omit<SettlementDraft, 'key'>;
}) {
  const today = todayLocal();
  const lastMonth = reportPeriodRange('last_month', today);
  const [userId, setUserId] = useState<string | undefined>(initial.userId);
  const [from, setFrom] = useState(initial.from ?? lastMonth.from);
  const [to, setTo] = useState(initial.to ?? lastMonth.to);
  const [settledOn, setSettledOn] = useState(today);
  const [method, setMethod] = useState<PartnerSettlementMethod>('cash');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const partner = userId ?? (partners.length === 1 ? partners[0]?.userId : undefined);
  const validRange = Boolean(from && to && to >= from);
  const preview = trpc.time.partnerSettlementPreview.useQuery(
    { userId: partner ?? '', from, to },
    { enabled: open && Boolean(partner) && validRange },
  );
  const invalidate = useInvalidateTime();
  const close = trpc.time.closePartnerSettlement.useMutation({
    onSuccess: async (s) => {
      toast.success(
        `Rozliczenie zamknięte: ${formatMoneyAuto(s.amount, s.currency)} (${PARTNER_SETTLEMENT_METHOD_LABELS[s.method]})`,
      );
      await invalidate();
      setNote('');
      onOpenChange(false);
    },
    onError: (err) => setError(errorMessage(err, 'Nie udało się zamknąć rozliczenia.')),
  });
  const data = preview.data;
  // Przegląd Fali 11: nierozliczone wpisy partnera poza wybranym okresem — podpowiedź z kwotą i „Ustaw okres”
  const owed = outstanding.find((o) => o.user.userId === partner);
  const owedRange = owed ? { from: owed.from, to: owed.to > today ? owed.to : today } : null;
  const outside = Boolean(owed && owedRange && data && owed.entryCount > data.entryCount);
  const canClose = Boolean(partner && data && data.entryCount > 0 && settledOn) && !close.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setError(null);
        onOpenChange(o);
      }}
      title="Nowe rozliczenie partnera"
      closeLabel="Zamknij okno"
      description="Godziny i kwota z nierozliczonych wpisów partnera w okresie (stawka partnera)."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            disabled={!canClose}
            loading={close.isPending}
            onClick={() => {
              if (!partner || !data) return;
              setError(null);
              close.mutate({
                userId: partner,
                from,
                to,
                entryIds: data.entries.map((e) => e.id),
                settledOn,
                method,
                ...(note.trim() ? { note: note.trim() } : {}),
              });
            }}
          >
            Zamknij rozliczenie
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Partner">
          <Select
            value={partner}
            onValueChange={setUserId}
            options={partners.map((p) => ({ value: p.userId, label: p.name }))}
            placeholder={partners.length === 0 ? 'Nikt nie ma stawki partnera' : 'Wybierz osobę…'}
            disabled={partners.length === 0}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Od">
            <DateInput value={from} onValueChange={setFrom} />
          </Field>
          <Field label="Do">
            <DateInput value={to} onValueChange={setTo} />
          </Field>
        </div>
        <div className="rounded-lg bg-surface px-4 py-3 text-body-s" aria-live="polite">
          {!partner ? (
            <span className="text-fg-muted">Wybierz partnera, żeby zobaczyć godziny.</span>
          ) : preview.isFetching && !data ? (
            <span className="text-fg-muted">Liczę…</span>
          ) : data ? (
            data.entryCount === 0 ? (
              <span className="text-fg-muted">Brak nierozliczonych wpisów ze stawką partnera w tym okresie.</span>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-fg-secondary">
                    {data.entryCount} {plural(data.entryCount, ['wpis', 'wpisy', 'wpisów'])} ·{' '}
                    <Mono>{formatClock(data.seconds)}</Mono>
                  </span>
                  <Mono className="text-body-m font-semibold text-fg">
                    {formatMoneyAuto(data.amount, data.currency)}
                  </Mono>
                </div>
                {data.withoutRate.entryCount > 0 && (
                  <span className="text-caption text-fg-muted">
                    <Mono>{formatClock(data.withoutRate.seconds)}</Mono> bez stawki partnera — nie wchodzi do
                    rozliczenia
                  </span>
                )}
              </div>
            )
          ) : null}
          {outside && owed && owedRange && (
            <div
              className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-line pt-3"
              role="note"
            >
              <span className="text-fg-secondary">
                Wszystkie nierozliczone: <Mono>{formatClock(owed.seconds)}</Mono> ·{' '}
                <Mono>{formatMoneyAuto(owed.amount, owed.currency)}</Mono> ({rangeLongLabel(owedRange)})
              </span>
              <Button
                variant="secondary"
                size="s"
                className={TOUCH_BUTTON}
                onClick={() => {
                  setFrom(owedRange.from);
                  setTo(owedRange.to);
                }}
              >
                Ustaw okres
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <Field label="Data rozliczenia" className="sm:max-w-[220px]">
            <DateInput value={settledOn} onValueChange={setSettledOn} />
          </Field>
          <Field label="Sposób">
            <Segmented<PartnerSettlementMethod>
              aria-label="Sposób rozliczenia"
              value={method}
              onValueChange={setMethod}
              options={METHOD_OPTIONS}
            />
          </Field>
        </div>
        <Field label="Notatka" hint="Opcjonalnie, np. „gotówka przy spotkaniu”.">
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </Field>
        {error && <FormMessage>{error}</FormMessage>}
      </div>
    </Dialog>
  );
}

function ReopenDialog({ settlement, onClose }: { settlement: PartnerSettlementDto | null; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const invalidate = useInvalidateTime();
  const reopen = trpc.time.reopenPartnerSettlement.useMutation({
    onSuccess: async () => {
      toast.success('Rozliczenie otwarte ponownie — wpisy wróciły do nierozliczonych');
      await invalidate();
      setReason('');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się otworzyć rozliczenia.')),
  });
  return (
    <Dialog
      open={settlement !== null}
      onOpenChange={(o) => !o && onClose()}
      title="Otworzyć rozliczenie ponownie?"
      description="Wpisy wrócą do nierozliczonych i będzie można je poprawić. Rozliczenie zostanie w historii jako otwarte ponownie."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="secondary"
            loading={reopen.isPending}
            onClick={() =>
              settlement &&
              reopen.mutate({
                id: settlement.id,
                version: settlement.version,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              })
            }
          >
            Otwórz ponownie
          </Button>
        </>
      }
    >
      <Field label="Powód" hint="Opcjonalnie, np. „korekta godzin”.">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
      </Field>
    </Dialog>
  );
}
